import { Plugin } from 'ckeditor5/src/core';

import { Widget } from 'ckeditor5/src/widget';

import EditableFieldCreateCommand from './commands/EditableFieldCreateCommand';
import EditableFieldGetContentCommand from './commands/EditableFieldGetContentCommand';
import EditableFieldSetContentCommand from './commands/EditableFieldSetContentCommand';
import EditableFieldSetIsUpdatingCommand from './commands/EditableFieldSetIsUpdatingCommand';
import EditableFieldDiffCommand from './commands/EditableFieldDiffCommand';
import EditableFieldDiffBulkCommand from './commands/EditableFieldDiffBulkCommand';

import {
	COMMANDS, COMPONENT_ATTR_CAN_REVIEW, COMPONENT_ATTR_EDITING_ENABLED,
	COMPONENT_ATTR_ID, COMPONENT_ATTR_NAME, COMPONENT_ATTR_NEW_VALUE, COMPONENT_ATTR_UPDATING,
	COMPONENT_NAME,
	COMPONENT_TITLE,
	OUT_COMMANDS
} from './constants';

import '../theme/editableField.css';
import { enableEditableFieldCommandsOnRestrictedMode } from './utils';
import { upcastEditableField } from './conversion/upcast';
import { downcastEditableField } from './conversion/downcast';
import EditableFieldEnabledCommand from './commands/EditableFieldEnabledCommand';

/**
 *
 * @extends module:core/plugin~Plugin
 */
export default class EditableFieldEditing extends Plugin {
	/**
	 *
	 * @returns {string}
	 */
	static get pluginName() {
		return 'EditableFieldEditing';
	}

	static get requires() {
		return [ Widget ];
	}

	init() {
		this._defineSchema();
		this._defineConverters();

		this.editor.commands.add( COMMANDS.CREATE, new EditableFieldCreateCommand( this.editor ) );
		this.editor.commands.add( COMMANDS.GET_CONTENT, new EditableFieldGetContentCommand( this.editor ) );
		this.editor.commands.add( COMMANDS.SET_CONTENT, new EditableFieldSetContentCommand( this.editor ) );
		this.editor.commands.add(
			COMMANDS.SET_IS_UPDATING,
			new EditableFieldSetIsUpdatingCommand( this.editor )
		);
		this.editor.commands.add( COMMANDS.DIFF, new EditableFieldDiffCommand( this.editor ) );
		this.editor.commands.add( COMMANDS.DIFF_BULK, new EditableFieldDiffBulkCommand( this.editor ) );
		this.editor.commands.add( COMMANDS.ENABLED, new EditableFieldEnabledCommand( this.editor ) );

		enableEditableFieldCommandsOnRestrictedMode( this.editor.plugins );

		const editableFieldCreateCommand = this.editor.commands.get( COMMANDS.CREATE );
		editableFieldCreateCommand.delegate( OUT_COMMANDS.CREATE ).to( this.editor );

		const editableFieldGetContentCommand = this.editor.commands.get( COMMANDS.GET_CONTENT );
		editableFieldGetContentCommand.delegate( OUT_COMMANDS.GET_CONTENT ).to( this.editor );

		const editableFieldSetContentCommand = this.editor.commands.get( COMMANDS.SET_CONTENT );
		editableFieldSetContentCommand.delegate( OUT_COMMANDS.SET_CONTENT ).to( this.editor );

		const editableFieldSetIsUpdatingCommand = this.editor.commands.get( COMMANDS.SET_IS_UPDATING );
		editableFieldSetIsUpdatingCommand.delegate( OUT_COMMANDS.SET_IS_UPDATING ).to( this.editor );

		const editableFieldDiffCommand = this.editor.commands.get( COMMANDS.DIFF );
		editableFieldDiffCommand.delegate( OUT_COMMANDS.DIFF ).to( this.editor );

		const editableFieldDiffBulkCommand = this.editor.commands.get( COMMANDS.DIFF_BULK );
		editableFieldDiffBulkCommand.delegate( OUT_COMMANDS.DIFF_BULK ).to( this.editor );

		const editableEnabledCommand = this.editor.commands.get( COMMANDS.ENABLED );
		editableEnabledCommand.delegate( OUT_COMMANDS.ENABLED ).to( this.editor );
	}

	destroy() {
		this.stopListening();
	}

	_defineSchema() {
		const schema = this.editor.model.schema;

		schema.register( COMPONENT_NAME, {
			allowWhere: '$text',
			isInline: true,
			isObject: true,
			allowChildren: [ COMPONENT_TITLE ],
			allowAttributesOf: '$text',
			allowAttributes: [
				COMPONENT_ATTR_EDITING_ENABLED,
				COMPONENT_ATTR_ID,
				COMPONENT_ATTR_NAME,
				COMPONENT_ATTR_UPDATING,
				COMPONENT_ATTR_NEW_VALUE,
				COMPONENT_ATTR_CAN_REVIEW
			]
		} );

		schema.register( COMPONENT_TITLE, {
			isLimit: true,
			allowIn: COMPONENT_NAME,
			allowAttributes: [ 'restrictedEditingException' ]
		} );

		schema.extend( '$text', { allowIn: COMPONENT_TITLE } );

		schema.addChildCheck( ( context, childDefinition ) => {
			if (
				context.endsWith( COMPONENT_TITLE ) &&
        ( childDefinition.name === COMPONENT_NAME || childDefinition.name === 'Checkbox' )
			) {
				return false;
			}
		} );

		schema.addAttributeCheck( context => {
			if ( context.endsWith( `${ COMPONENT_TITLE } $text` ) ) {
				return false;
			}
		} );
	}

	/**
   * https://ckeditor.com/docs/ckeditor5/latest/assets/img/upcast-pipeline.svg
   * -------------------------- MODEL ------------------------------
   * _______________________________________________________________
   *---------- In -------------- Out ------|---------- In ----------
   *---------- | ---------------- | -------|---------- | -----------
   *---------- | ---------------- | -------|---------- | -----------
   *--- data downcast ---- data upcast ----|-- editing downcast ----
   * ---------- | ---------------- | ------|---------- | -----------
   * ---------- | ---------------- | ------|---------- | -----------
   * --------- Out ------------- In -------|--------- Out ----------
   * ------ data view  --------------------|----- editing view -----
   */
	_defineConverters() {
		const editor = this.editor;
		const conversion = this.editor.conversion;

		conversion
			.for( 'upcast' )
			.elementToElement( {
				view: { name: 'span', classes: [ 'editable-field' ] },
				model: upcastEditableField
			} )
			.elementToElement( {
				view: { name: 'span', classes: [ 'editable-field-title' ] },
				model: COMPONENT_TITLE
			} );

		conversion.for( 'editingDowncast' ).elementToElement( {
			model: {
				name: COMPONENT_NAME,
				attributes: [
					COMPONENT_ATTR_EDITING_ENABLED,
					COMPONENT_ATTR_UPDATING,
					COMPONENT_ATTR_NEW_VALUE,
					COMPONENT_ATTR_CAN_REVIEW
				],
				children: [ '$text' ]
			},
			view: downcastEditableField( editor, { asWidget: true } )
		} );

		if ( editor.plugins.has( 'RestrictedEditingModeEditing' ) ) {
			conversion.for( 'dataDowncast' ).markerToElement( {
				model: 'restrictedEditingException',
				view: ( markerData, { writer } ) => {
					return writer.createText( '' );
				}
			} );
		}

		conversion.for( 'dataDowncast' ).elementToElement( {
			model: COMPONENT_NAME,
			view: downcastEditableField( editor, { asWidget: false } )
		} );
	}
}
